import { TwilioCountriesAbbr } from '@/core/country-constants';
import {
    DlcRegistrationStageConstants,
    DlcRegistrationStatusConstants
} from '@/organizations/corporate/constants/dlc-registration-constants';
import { ClientInfoDto } from '@/organizations/corporate/models/client-info';

/**
 * Handles utility functionality for client.
 */
export class ClientUtils {
    /**
     * Get the business name tooltip message.
     *
     * @param country
     */
    public getBusinessNameTooltip(country: TwilioCountriesAbbr | string): string {
        switch (country) {
            case TwilioCountriesAbbr.UNITED_STATES:
                return 'This must exactly match the business name on the business formation documents submitted to your Secretary of State in order to enable the Texting and/or Call Recording features.';
            case TwilioCountriesAbbr.CANADA:
                return 'This must exactly match the business name on the business formation documents submitted to Corporations Canada in order to enable the Texting and/or Call Recording features.';
            case TwilioCountriesAbbr.AUSTRALIA:
                return 'This must exactly match the business name on the business formation documents submitted to the Australian Securities & Investments Commission in order to enable the Texting and/or Call Recording features.';
            case TwilioCountriesAbbr.UNITED_KINGDOM:
                return 'This must exactly match the business name on the business formation documents submitted to Companies House in order to enable the Texting and/or Call Recording features.';
            case TwilioCountriesAbbr.NEW_ZEALAND:
                return 'This must exactly match the business name on the business formation documents submitted to the Companies Office in order to enable the Texting and/or Call Recording features.';
            case TwilioCountriesAbbr.INDIA:
            default:
                return 'This must match exactly the business name on your formation documents to enable Texting and/or Call Recording features.';
        }
    }

    /**
     * Get the business registration label.
     *
     * @param country
     */
    public getBusinessRegistrationLabel(country: TwilioCountriesAbbr | string): string {
        switch (country) {
            case TwilioCountriesAbbr.UNITED_STATES:
                return 'Employer Identification Number (EIN)';
            case TwilioCountriesAbbr.CANADA:
                return 'Business Number (BN)';
            case TwilioCountriesAbbr.AUSTRALIA:
                return 'Australian Business Number (ABN)';
            case TwilioCountriesAbbr.UNITED_KINGDOM:
                return 'Unique Taxpayer Reference (UTR)';
            case TwilioCountriesAbbr.NEW_ZEALAND:
                return 'New Zealand Business Number (NZBN)';
            case TwilioCountriesAbbr.INDIA:
                return 'Permanent Account Number (PAN)';
            default:
                return 'Business Registration Number';
        }
    }

    /**
     * Get the business registration validation rules.
     *
     * @param country
     */
    public getBusinessRegistrationValidationRules(country: TwilioCountriesAbbr | string) {
        switch (country) {
            case TwilioCountriesAbbr.UNITED_STATES:
                return [(value: string) => /^[0-9]{2}-[0-9]{7}$/.test(value) || 'Your EIN should be formatted XX-XXXXXXX'];
            case TwilioCountriesAbbr.CANADA:
                return [(value: string) => /^[0-9]{9}$/.test(value) || 'Your BN should be 9 digits long'];
            case TwilioCountriesAbbr.AUSTRALIA:
                return [
                    (value: string) =>
                        /^[0-9]{2} [0-9]{3} [0-9]{3} [0-9]{3}$/.test(value) || 'Your ABN should be formatted XX XXX XXX XXX'
                ];
            case TwilioCountriesAbbr.UNITED_KINGDOM:
                return [
                    (value: string) => {
                        // Normalize input: remove spaces, special characters, and ensure lowercase for "k"
                        const normalized = value.replace(/\s+/g, '').toLowerCase();

                        // Check for valid UTR format
                        const isValidUTR =
                            /^k?\d{10}k?$/.test(normalized) || // 10 digits, optionally starting or ending with "k"
                            /^k?\d{13}k?$/.test(normalized); // 13 digits, optionally starting or ending with "k"

                        return (
                            isValidUTR ||
                            'Your UTR should be 10 or 13 digits, with or without spaces, and may start or end with "K".'
                        );
                    }
                ];
            case TwilioCountriesAbbr.NEW_ZEALAND:
                return [(value: string) => /^[0-9]{13}$/.test(value) || 'Your NZBN should be 13 digits long'];
            case TwilioCountriesAbbr.INDIA:
            default:
                return [(value: string) => !!value || 'This field is required'];
        }
    };

    /**
     * Get the business registration tooltip message.
     *
     * @param country
     */
    public getBusinessRegistrationTooltip(country: TwilioCountriesAbbr | string): string {
        switch (country) {
            case TwilioCountriesAbbr.UNITED_STATES:
                return 'Required to enable the Texting and/or Call Recording features. Note: Include the dash.';
            case TwilioCountriesAbbr.AUSTRALIA:
                return 'Required to enable the Texting and/or Call Recording features. Note: Include the spaces.';
            case TwilioCountriesAbbr.UNITED_KINGDOM:
                return 'Required to enable the Texting and/or Call Recording features. Note: Include spaces if your number has them.';
            case TwilioCountriesAbbr.CANADA:
            case TwilioCountriesAbbr.NEW_ZEALAND:
            case TwilioCountriesAbbr.INDIA:
            default:
                return 'Required to enable the Texting and/or Call Recording features.';
        }
    }

    /**
     * Determines whether to show the 'Apply for CRM System Phone Number(s)' button.
     * The button is shown if:
     * - The DLC registration process hasn't started (null status), is in draft, or was rejected.
     * - OR the DLC registration is fully approved (APPROVED status and A2P_APPROVED stage),
     * but not all accessible centers currently have a CRM phone number assigned.
     *
     * @param dlcRegistrationStatus - The current status of the DLC registration.
     * @param dlcRegistrationStage - The current stage of the DLC registration.
     * @param allCentersHaveCrmPhone - Boolean indicating if all centers already have a CRM phone.
     * @returns True if the button should be shown, false otherwise.
     */
    public showApplyCrmNumberButton(
        dlcRegistrationStatus: DlcRegistrationStatusConstants | null,
        dlcRegistrationStage: DlcRegistrationStageConstants | null,
        allCentersHaveCrmPhone: boolean
    ): boolean {
        // Condition 1: Show if status indicates application is possible/needed again
        const showForInitialOrRejectedStatus =
            !dlcRegistrationStatus || // No status yet
            dlcRegistrationStatus === DlcRegistrationStatusConstants.DRAFT ||
            dlcRegistrationStatus === DlcRegistrationStatusConstants.REJECTED;

        // Condition 2: Show if fully approved BUT not all centers have phones yet
        // (Allows applying for numbers for the remaining centers)
        const showForApprovedButMissingPhones =
            dlcRegistrationStatus === DlcRegistrationStatusConstants.APPROVED &&
            dlcRegistrationStage === DlcRegistrationStageConstants.A2P_APPROVED &&
            !allCentersHaveCrmPhone; // Check if phones are still needed

        // Return true if either condition is met
        return showForInitialOrRejectedStatus || showForApprovedButMissingPhones;
    }

    /**
     * Determines whether *any* DLC status message should be displayed based on current state.
     * Shows a message for DRAFT, REJECTED, or PROCESSING statuses.
     * For the fully APPROVED/A2P_APPROVED state, it only shows the message *if*
     * all centers currently have a CRM phone number assigned.
     * Hides message for PENDING status or if status/stage is missing.
     *
     * @param dlcRegistrationStatus - The current status of the DLC registration.
     * @param dlcRegistrationStage - The current stage of the DLC registration.
     * @param allCentersHaveCrmPhone - Boolean indicating if all centers already have a CRM phone.
     * @returns True if a status message should be shown, false otherwise.
     */
    public showDlcStatusMessage(
        dlcRegistrationStatus: DlcRegistrationStatusConstants | null,
        dlcRegistrationStage: DlcRegistrationStageConstants | null,
        allCentersHaveCrmPhone: boolean // Added parameter
    ): boolean {
        // Never show if essential status/stage info is missing
        if (!dlcRegistrationStatus || !dlcRegistrationStage) {
            return false;
        }

        // Handle the specific condition for the fully approved state
        if (
            dlcRegistrationStatus === DlcRegistrationStatusConstants.APPROVED &&
            dlcRegistrationStage === DlcRegistrationStageConstants.A2P_APPROVED
        ) {
            // Per requirement: Only show the "Approved" status message once
            // all centers actually have their phone numbers assigned.
            return allCentersHaveCrmPhone; // Use the boolean directly
        }

        // Show for these other actionable/informative statuses
        return dlcRegistrationStatus === DlcRegistrationStatusConstants.REJECTED ||
            dlcRegistrationStatus === DlcRegistrationStatusConstants.DRAFT ||
            dlcRegistrationStatus === DlcRegistrationStatusConstants.PROCESSING;
    }

    /**
     * Show the status label based on the dlc registration status and dlc registration stage
     *
     * @param dlcRegistrationStatus
     * @param dlcRegistrationStage
     * @param hasSingleCenter
     */
    public getStatusLabel(dlcRegistrationStatus: DlcRegistrationStatusConstants | null, dlcRegistrationStage: DlcRegistrationStageConstants | null, hasSingleCenter: boolean): string {
        if (!dlcRegistrationStatus || !dlcRegistrationStage) {
            return '';
        }

        const phoneNumberText = hasSingleCenter ? 'Number' : 'Numbers';
        const phoneNumber = this.getCrmPhoneNumberPluralizedLabel(hasSingleCenter);

        if (dlcRegistrationStatus === DlcRegistrationStatusConstants.APPROVED && dlcRegistrationStage === DlcRegistrationStageConstants.A2P_APPROVED) {
            return `Your CRM System Phone ${phoneNumberText} been approved. Click "Edit" on the Location Information table to find the "CRM System Phone" and related settings under "Location Phone".`;
        }

        if (dlcRegistrationStatus === DlcRegistrationStatusConstants.REJECTED) {
            return `Your CRM System Phone ${phoneNumberText} rejected, likely because the data provided in the "Account Information" section was inaccurate. Correct any errors and submit again, or contact Customer Support for additional assistance.`;
        }
        return `The application process to obtain ${phoneNumber} has been submitted to our third party provider. Approval can take a few days to a few weeks. This message will update once the application has been processed.`;
    }

    /**
     * Clean social media links by removing root parts like domain prefixes.
     *
     * @param dto The client info DTO to clean (modified in place)
     */
    public cleanSocialMediaLinks(dto: ClientInfoDto): void {
        if (!dto.social_media) {
            return;
        }

        // Define all possible prefixes once
        const prefixes = [
            'http://',
            'https://',
            'www.facebook.com/',
            'facebook.com/',
            'www.youtube.com/',
            'youtube.com/',
            'www.pinterest.com/',
            'pinterest.com/',
            'www.twitter.com/',
            'twitter.com/',
            'www.instagram.com/',
            'instagram.com/',
            'www.linkedin.com/',
            'linkedin.com/',
            'www.yelp.com/',
            'yelp.com/'
        ];

        // Clean each social media platform
        if (dto.social_media.facebook) {
            dto.social_media.facebook = this.removeAllPrefixes(dto.social_media.facebook, prefixes);
        }

        if (dto.social_media.twitter) {
            dto.social_media.twitter = this.removeAllPrefixes(dto.social_media.twitter, prefixes);
        }

        if (dto.social_media.youtube) {
            dto.social_media.youtube = this.removeAllPrefixes(dto.social_media.youtube, prefixes);
        }

        if (dto.social_media.linkedin) {
            dto.social_media.linkedin = this.removeAllPrefixes(dto.social_media.linkedin, prefixes);
        }

        if (dto.social_media.instagram) {
            dto.social_media.instagram = this.removeAllPrefixes(dto.social_media.instagram, prefixes);
        }

        if (dto.social_media.pinterest) {
            dto.social_media.pinterest = this.removeAllPrefixes(dto.social_media.pinterest, prefixes);
        }

        if (dto.social_media.yelp !== null) {
            dto.social_media.yelp = this.removeAllPrefixes(dto.social_media.yelp, prefixes);
        }

    }

    /**
     * Helper method to remove all prefixes from a string
     *
     * @param value The value to clean
     * @param prefixes Array of prefixes to remove
     * @returns The cleaned string
     */
    private removeAllPrefixes(value: string, prefixes: string[]): string {
        if (!value) return value;

        let result = value;
        let wasChanged = true;

        // Keep removing prefixes until no more can be removed
        // This handles cases with multiple prefixes
        while (wasChanged) {
            wasChanged = false;
            for (const prefix of prefixes) {
                if (result.startsWith(prefix)) {
                    result = result.substring(prefix.length);
                    wasChanged = true;
                    break;
                }
            }
        }

        return result;
    }

    /**
     * Validates if the essential fields within the ClientInfoDto,
     * particularly those under the "Account Information" section and
     * prerequisites for features like CRM Phone Number, appear to be filled
     * and correctly formatted according to their defined rules.
     *
     * Note: This performs basic format checks (email, country-specific ID)
     * but relies on components for deeper validation (e.g., phone number validity).
     *
     * @param dto The ClientInfoDto object to validate. Can be null.
     * @returns True if all checked required fields are valid according to the rules, false otherwise.
     */
    public validateAccountInfo(dto: ClientInfoDto): boolean {
        // --- Helper functions mirroring the validation rules ---
        const checkRequired = (v: string | number | undefined | null): boolean => !!v;
        const checkEmail = (v: string | undefined | null): boolean => !!v && /.+@.+\..+/.test(v);

        // --- Field-by-field validation checks ---

        // Website URL (Prerequisite from tooltip)
        if (!checkRequired(dto.url)) return false;

        // Billing Account Name
        if (!checkRequired(dto.billing_account_name)) return false;

        // Billing Address (Check object exists AND fields are required)
        if (!dto.billing_address ||
            !checkRequired(dto.billing_address.address1) ||
            !checkRequired(dto.billing_address.locality) ||
            !checkRequired(dto.billing_address.region) ||
            !checkRequired(dto.billing_address.postcode)) {
            return false;
        }

        // Billing Contact
        if (!checkRequired(dto.billing_contact_first_name)) return false;
        if (!checkRequired(dto.billing_contact_last_name)) return false;
        // Check phone object
        if (!checkRequired(dto.billing_contact_phone.number_e164)) return false;
        // Check email format
        if (!checkEmail(dto.billing_contact_email)) return false;
        if (!checkRequired(dto.billing_contact_job_title)) return false;
        if (!checkRequired(dto.billing_contact_job_position)) return false;

        // Business Details
        if (!checkRequired(dto.business_type)) return false;
        // Country is crucial and required for subsequent validation
        if (!checkRequired(dto.iso_country)) return false;
        if (!checkRequired(dto.business_regions_of_operation)) return false;

        // Sales Contact
        if (!checkRequired(dto.sales_contact_first_name)) return false;
        if (!checkRequired(dto.sales_contact_last_name)) return false;
        // Check phone object exists AND number is required
        if (!checkRequired(dto.sales_contact_phone.number_e164)) return false;
        // Check email format
        if (!checkEmail(dto.sales_contact_email)) return false;

        // Legal/Registration
        if (!checkRequired(dto.legal_business_name)) return false;

        // Business Registration Identifier (Dynamic Validation)
        // First check if required (it is)
        if (!checkRequired(dto.business_registration_identifier)) return false;
        // Then, apply country-specific rules using the existing utility method
        const regIdRules = this.getBusinessRegistrationValidationRules(dto.iso_country);
        // The method returns an array with one validation function
        if (regIdRules && regIdRules.length > 0) {
            const validationFn = regIdRules[0];
            const validationResult = validationFn(dto.business_registration_identifier || ''); // Execute the rule function

            // The rule function returns `true` if valid, or an error `string` if invalid.
            if (typeof validationResult === 'string') {
                // If the result is a string, validation failed.
                return false;
            }
            // If the result was true, validation passed for this field.
        }
        // If all the above checks passed, the account info is considered valid for this purpose.
        return true;
    }

    /**
     * Returns the appropriate label for the "Apply for CRM System Phone Numbers" button
     * based on the number of centers.
     *
     * @param hasSingleCenter Has a single center
     * @returns The formatted button label string
     */
    public getCrmPhoneNumberPluralizedLabel(hasSingleCenter: boolean): string {
        return hasSingleCenter
            ? 'a CRM System Phone Number'
            : 'CRM System Phone Numbers';
    }

    /**
     * Returns the appropriate label for the "Apply for CRM System Phone Numbers" button
     * based on the number of centers.
     *
     * @param hasSingleCenter Has a single center
     * @returns The formatted button label string
     */
    public getClientCallSettingsModalDescription(hasSingleCenter: boolean): string {
        return hasSingleCenter
            ? 'Click PROCEED to get a System Phone Number. You can have only one System Phone Number. The phone number will ' +
            'appear on the Edit Location window once approved.'
            : 'Click PROCEED to get a System Phone Number for each location. Each location can have only one System Phone ' +
            'Number. The phone numbers will appear on the Edit Location window once approved.';
    }

}
